/* eslint-disable @typescript-eslint/camelcase */

import React from 'react';
import { withTheme } from 'styled-components';
import { resolveBp } from '@deloitte-digital-au/dd-breakpoint-container';
import { useIdleTimer } from 'react-idle-timer';
import { useHistory, useLocation } from 'react-router-dom';
import { isJwtExpired } from 'jwt-check-expiration';

import config from 'jss-boilerplate/temp/config.js';

import { IThemeObject } from 'pods/theme';

import LayoutPadding from 'core/components/LayoutPadding';
import { ICONS } from 'core/components/Icon';
import {
	getProductClassificationLabel,
	formatCurrency,
} from 'core/utils/global-utils';
import { AppBreakpoint } from 'src/_AppContainer';
import { getCleanImageSrc, JssLinkType } from 'core/utils/sitecore-utils';
import {
	useAppContext,
	useMemberContext,
	useTrackEvent,
	usePopulateBalanceForDefaultAccount,
	useGetFinancialYears,
	useStickyPosition,
} from 'core/hooks';
import { getSelectedMemberAccount } from 'core/sitecore/member';
import {
	setLastUrlAccess,
	getSelectedAccountBalance,
	setDisplayLogoutSuccessFlag,
	setSessionTimeLeft,
	getSessionTimeLeft,
	clearSessionCookie,
	getAccessToken,
	getRefreshToken,
	setAccessToken,
	setRefreshToken,
	clearLocalStorage,
} from 'core/browserStorage';
import { ACCOUNT_STATUS, LOGOUT_STATUS, SITECORE_PAGES } from 'core/constants';
import { GetTalSsoTokenPathParams } from 'core/api/client/InsuranceApi';
import { memberRefreshToken } from 'core/api/sitecore.api';

import * as S from './AuthenticatedHeader.style';
import CONTENT from '../../Header.content';
import { IFields, MenuItem } from '../../Header.sitecore';
import AdditionalLinksGroup from '../AdditionalLinksGroup';
import DesktopHeader from '../DesktopHeader';
import MobileHeader from '../MobileHeader';
import {
	checkIfPageIsAllowed,
	checkNavigationFilterByAccountType,
	filterLevel1ItemsByAccountType,
	filterLevel2ItemsByAccountType,
	isPendingProfile,
} from './AuthenticatedHeader.helpers';
import { BalanceProps } from '../DesktopHeader/DesktopNavItem';
import api from '../../api';
import { useShowComponentWithDelay } from '../../../hook';

const MOBILE_BP = '0,m';

interface IProps {
	theme: IThemeObject;
	fields: IFields;
	dataTargetRoot?: string;
	showAccountSwitcher: boolean;
	logoUrl: JssLinkType;
}

const baseInsurance = `${config.australianSuperApi?.apiDomain}${config.australianSuperApi?.apiPathInsurance}`;

const AuthenticatedHeader = ({
	fields,
	dataTargetRoot = 'header_authenticated',
	showAccountSwitcher = false,
	logoUrl,
}: IProps) => {
	// Start of [Session logout]
	// TODO
	// Refactor session logout section if there will be another approach in the future.
	// For now we are using react-idle-timer to see any inactivity along with localStorage flaggings on AppContainer.

	// This will wait for MPORTAL-6779 to be fully completed but all FED effort now are done.
	// [BR03] and [BR05]-(different browsers) based on https://confluence.australiansuper.net.au/display/MemberPortalV5/T07.E05.P01+-+Display+Logout+Success

	const isEditing =
		(window?.sitecoreContext ?? true) &&
		(window?.sitecoreContext?.pageEditing ?? true);

	const {
		backEndRequest,
		handleFYRequest,
		handleTalSsoUrl,
		handleIsTalSsoUrlLoading,
		triggerTalSsoUrl,
		handleTriggerTalSsoUrl,
		handleTalSsoError,
	} = useAppContext();
	const { pathname: lastLocationBeforeInactivity } = useLocation();
	const mainHeaderContentRef = React.useRef<HTMLElement>(null);
	const navRef = React.useRef<HTMLElement>(null);
	const { StickyWrapper, isFixedOnTop } = useStickyPosition(navRef);
	// Analytics spreadsheet:
	// - Section: 'Account access'
	// - ID: 07.5.1
	const { trackEvent: logoutTrackEvent } = useTrackEvent({
		event_section: 'account access',
		event_type: 'logout',
		event_title: 'your session has expired',
	});

	const { reset, getRemainingTime } = useIdleTimer({
		// [BR01] 20 minutes timeout
		timeout: getSessionTimeLeft() ?? 1200000,
		// Remove all default events that resets idle timer since we only want to reset on back-end call
		events: [],
		onIdle: (): void => {
			const scEpxpEditor = document.querySelectorAll('#scPageExtendersForm');
			if (scEpxpEditor.length === 0) {
				logoutTrackEvent({
					event_label: 'start',
					event_message: '',
					event_trigger: 'screen load',
				});
				sessionStorage.clear();
				clearLocalStorage();
				clearSessionCookie();
				setDisplayLogoutSuccessFlag(LOGOUT_STATUS.IDLE);
				// Set the last URL here
				// If not set here then '/personal/logout' will always be the value since this will go to RouteHandler.js
				setLastUrlAccess(lastLocationBeforeInactivity);
				window.location.assign(SITECORE_PAGES.LOGOUT);
			}
		},
		crossTab: {
			emitOnAllTabs: true,
		},
	});

	/* eslint-disable react-hooks/exhaustive-deps */
	React.useEffect(() => {
		// Reset the timer if there is a change to backEndRequest since we are toggling backEndRequest every --
		// restful-react and axios call
		if (backEndRequest) {
			reset();
		}

		if (getRemainingTime()) {
			setSessionTimeLeft(getRemainingTime());
		}
	}, [backEndRequest, getRemainingTime]);
	// End of [Session logout]

	const { accounts = [] } = useMemberContext();
	const [newAccountBalance, setNewAcctBalance] = React.useState(
		getSelectedAccountBalance() ?? 0,
	);
	const [balanceData, setBalanceData] = React.useState<BalanceProps>();
	const selectedAccount = getSelectedMemberAccount(accounts);
	const productName = selectedAccount?.product_name ?? '';
	const accountNumber = selectedAccount?.account_number ?? '';
	const accountStatus = selectedAccount?.account_status ?? '';
	const productClassification = selectedAccount?.product_classification ?? '';

	const { balance, loading, error } = usePopulateBalanceForDefaultAccount(
		false,
	);

	// References Hooks in getting financial year
	const {
		financialYearsRefResponse,
		isFinancialYearsRefLoading,
	} = useGetFinancialYears(accountNumber, productName);

	React.useEffect(() => {
		if (!loading && !error) {
			setBalanceData({ productClassification, balance, loading, error });
			setNewAcctBalance(String(balance ?? 0));
		}

		if (!isFinancialYearsRefLoading) {
			handleFYRequest(financialYearsRefResponse);
		}
	}, [loading, isFinancialYearsRefLoading]);

	// Start: Calling tal-sso here instead on each Content block component
	const {
		mutate: getTalSsoToken,
		loading: getTalSsoTokenLoading,
	} = api.useGetTalSsoToken({
		base: baseInsurance,
		product_name: productName as GetTalSsoTokenPathParams['product_name'],
		account_number: accountNumber as GetTalSsoTokenPathParams['account_number'],
	});

	const getTalData = async () => {
		try {
			return await getTalSsoToken();
		} catch (error) {
			handleTalSsoError(true);
			throw error;
		}
	};

	React.useEffect(() => {
		if (triggerTalSsoUrl) {
			try {
				getTalData()
					.then(response => {
						handleTalSsoUrl(response?.data?.redirect_url);
					})
					.catch(() => {
						handleTalSsoError(true);
					})
					.finally(() => {
						// Note: Set back to default (false) in case of refetching
						handleTriggerTalSsoUrl(false);
					});
			} catch (e) {}
		}
	}, [triggerTalSsoUrl]);

	React.useEffect(() => {
		// Note: For caterpillar loader
		handleIsTalSsoUrlLoading(getTalSsoTokenLoading);
	}, [getTalSsoTokenLoading]);

	// End: Calling tal-sso here instead on each Content block component

	const additionalLinksProps = {
		profile: {
			level1ProfileButton: fields?.level1ProfileButton,
			secondaryMenuProfileLinks: fields?.secondaryMenuProfileLinks,
		},
		notifications: {
			level1NotificationButton: fields?.level1NotificationButton,
			notificationsEmptyStateMessage: fields?.notificationsEmptyStateMessage,
			unreadNotificationLabel: fields?.unreadNotificationLabel,
			unreadNotificationsTitle: fields?.unreadNotificationsTitle,
			seeAllNotificationsLink: fields?.seeAllNotificationsLink,
			seeAllNotificationsLinkForMobile:
				fields?.seeAllNotificationsLinkForMobile,
			notificationsEmptyStateIllustration:
				fields?.notificationsEmptyStateIllustration,
		},
	};

	// Nav link for the account switcher
	const accountSwitcherItem: MenuItem = {
		id: 'account-switcher',
		navigationTitle: {
			value: `${getProductClassificationLabel(
				productClassification,
			)}|(${formatCurrency(balance)})`,
		},
		children: [],
		url: '',
		pageTitle: { value: '' },
		pageDescription: { value: '' },
		hideFromNavigation: { boolValue: false },
		accessType: '',
	};

	// Extra state to counter re-render before useMemo computation below
	const [isReady, setIsReady] = React.useState(false);

	React.useEffect(() => {
		if (balance !== undefined) {
			setIsReady(true);
		}
	}, [balance]);

	const level1MenuItems = React.useMemo(() => {
		const { additionalData } = fields;
		// Nav link to the home page
		const homeItem = { ...additionalData.data.item, children: [] };
		// Filter direct children if user is having pending member status
		let filteredLevel1Items = additionalData.data.item.children;

		filteredLevel1Items = isPendingProfile(
			newAccountBalance,
			productClassification,
			accountStatus,
		)
			? filterLevel1ItemsByAccountType(
					productName,
					additionalData.data.item.children,
			  )
			: additionalData.data.item.children;

		// Direct children of the home page
		const restItemsRaw = (filteredLevel1Items ?? []).filter(
			(item: any) =>
				!item?.hideFromNavigation?.boolValue &&
				!checkNavigationFilterByAccountType(productName, item?.acctType),
		);
		// Filter out level 2 items that should be hide from navigation
		const restItems = restItemsRaw.map(item => {
			const visibleChildren =
				item.children?.filter(
					({ hideFromNavigation, acctType }) =>
						!hideFromNavigation.boolValue &&
						!checkNavigationFilterByAccountType(productName, acctType!),
				) ?? [];

			const accountMenuItemsFilter = isPendingProfile(
				newAccountBalance,
				productClassification,
				accountStatus,
			)
				? filterLevel2ItemsByAccountType(
						visibleChildren,
						productClassification,
						newAccountBalance,
				  )
				: visibleChildren;

			return { ...item, children: accountMenuItemsFilter };
		});

		return [homeItem, ...restItems];
	}, [fields, newAccountBalance]);

	React.useEffect(() => {
		const menuItems = fields?.additionalData?.data?.item?.children;
		if (level1MenuItems && menuItems && menuItems?.length > 3) {
			// This will check if user is allowed to access a URL from its deeplinking origin
			const currentUrl = window?.location?.pathname ?? '/';
			const isAllowedToRedirect = checkIfPageIsAllowed(
				productName,
				window?.location?.pathname,
				menuItems,
			);
			// URLs below should be exempted to deep linking validation
			const noEffectUrls = [
				`${SITECORE_PAGES.CONTENT_NOT_AVAILABLE}`,
				`${SITECORE_PAGES.DASHBOARD}`,
				`${SITECORE_PAGES.LOGIN}`,
				`${SITECORE_PAGES.LOGOUT}`,
			];

			if (!isAllowedToRedirect && !noEffectUrls.includes(currentUrl)) {
				window.location.assign(`${SITECORE_PAGES.CONTENT_NOT_AVAILABLE}`);
			}
		}
	}, [level1MenuItems]);

	// NOTE: Only refresh token if current token is expired
	const accessToken = getAccessToken();
	const rToken = getRefreshToken();
	let isAccesTokenExpired = false;
	if (!isEditing) isAccesTokenExpired = isJwtExpired(accessToken);

	// NOTE: This is used by mobile app to disable token refresh on a specific page they want if param is present.
	const authHeaderSearchParams = new URLSearchParams(window.location.search);
	const appviewParamValue =
		authHeaderSearchParams.get('appview')?.toString() === 'true' ? true : false;

	React.useEffect(() => {
		if (isAccesTokenExpired && !isEditing && !appviewParamValue) {
			memberRefreshToken(rToken)
				.then(result => {
					if (result.status === 200) {
						const { accessToken, refreshToken, isValid } = result?.data;

						if (isValid === true) {
							setAccessToken(accessToken);
							setRefreshToken(refreshToken);
						}
					}
				})
				.catch(err => {
					console.log('error in renewing token ', err);
				});
		}
	}, [isAccesTokenExpired, appviewParamValue, isEditing]);

	const history = useHistory();
	const currentPath = history.location?.pathname ?? '';
	const [isLockedAccountPage, setIsLockedAccountPage] = React.useState(false);

	React.useEffect(() => {
		if (
			currentPath?.toLocaleLowerCase() === SITECORE_PAGES.ACCOUNT_LOCKED_SCREEN
		) {
			setIsLockedAccountPage(true);
		}
	}, [currentPath]);

	const { isToShow: isToShowAuthenticatedHeader } = useShowComponentWithDelay(
		'headerHasBeenDelayedOnceKey',
	);

	return (
		<AppBreakpoint.Consumer>
			{(appBp: string) => {
				const isMobile = resolveBp(MOBILE_BP, appBp);

				return (
					isToShowAuthenticatedHeader && (
						<StickyWrapper
							fallbackHeight={0}
							isFixedOnTop={isMobile ? isFixedOnTop : false}
							showOrangeBar
						>
							<S.Wrapper
								data-target-id={dataTargetRoot}
								ref={navRef as React.RefObject<HTMLDivElement>}
							>
								<S.OrangeLine />
								<S.Header>
									<LayoutPadding>
										<S.Content
											ref={
												mainHeaderContentRef as React.RefObject<HTMLDivElement>
											}
										>
											<S.Logo
												data-target-id={`${dataTargetRoot}--logo`}
												{...(isMobile ? { className: '-mobile' } : {})}
											>
												{(fields?.headerLogo && (
													<a href={logoUrl.value.href}>
														<img
															src={getCleanImageSrc(
																fields?.headerLogo?.value?.src,
															)}
															alt={CONTENT.LOGO_ALT}
														/>
													</a>
												)) || <></>}
											</S.Logo>
											{!isMobile && !isLockedAccountPage && (
												<AdditionalLinksGroup {...additionalLinksProps} />
											)}
										</S.Content>
									</LayoutPadding>
									{/* TODO: if sitecore field is ready put these (fields.accountSwitcherIcon.value) on both line 124 and 132  */}
									{accountStatus !== ACCOUNT_STATUS.PENDING &&
										!loading &&
										!error &&
										isReady &&
										!isLockedAccountPage &&
										(isMobile ? (
											<MobileHeader
												menuItems={level1MenuItems}
												{...additionalLinksProps}
												accountSwitcherItem={accountSwitcherItem}
												accountSwitcherIcon={ICONS.SWITCH_CLOSED}
												showAccountSwitcher={showAccountSwitcher}
												balanceData={balanceData}
												mainHeaderContentRef={mainHeaderContentRef}
											/>
										) : (
											<DesktopHeader
												menuItems={[accountSwitcherItem, ...level1MenuItems]}
												showAccountSwitcher={showAccountSwitcher}
												accountSwitcherIcon={ICONS.SWITCH_CLOSED}
												balanceData={balanceData}
											/>
										))}
								</S.Header>
							</S.Wrapper>
						</StickyWrapper>
					)
				);
			}}
		</AppBreakpoint.Consumer>
	);
};

export default withTheme(AuthenticatedHeader);
